import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import SortableTableCell from "../../components/layout/SortableTableCell";
import {
  auditLogsAPI,
  AuditLogEntry,
  AuditLogSortField,
} from "../../services/auditLogsAPI";
import { useTableSort } from "../../hooks/useTableSort";

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const formatWhen = (value: string) => {
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm:ss");
  } catch {
    return value;
  }
};

const AuditLogDetailDialog: React.FC<{
  logId: string | null;
  onClose: () => void;
}> = ({ logId, onClose }) => {
  const { data: log, isLoading, error } = useQuery({
    queryKey: ["audit-log", logId],
    queryFn: () => auditLogsAPI.getAuditLogById(logId!),
    enabled: Boolean(logId),
  });

  const bodyJson = log?.requestBody
    ? JSON.stringify(log.requestBody, null, 2)
    : null;

  return (
    <Dialog open={Boolean(logId)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Audit log details</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        {isLoading && (
          <Typography color="text.secondary">Loading…</Typography>
        )}
        {error && (
          <Alert severity="error">
            {(error as Error).message || "Failed to load details"}
          </Alert>
        )}
        {log && (
          <>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Activity
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <DetailRow label="When" value={formatWhen(log.createdAt)} />
              <DetailRow
                label="Status"
                value={
                  <Chip
                    label={log.success ? "Success" : "Failed"}
                    size="small"
                    color={log.success ? "success" : "error"}
                  />
                }
              />
              <DetailRow label="Action" value={log.action} />
              <DetailRow label="Resource" value={log.resource} />
              <DetailRow label="Resource ID" value={log.resourceId} />
              <DetailRow label="HTTP method" value={log.httpMethod} />
              <DetailRow label="Path" value={log.path} />
              <DetailRow label="Response status" value={log.responseStatus} />
              <DetailRow label="IP address" value={log.ipAddress} />
              <DetailRow
                label="User agent"
                value={
                  <Typography variant="caption" component="span">
                    {log.userAgent || "—"}
                  </Typography>
                }
              />
              {log.errorMessage && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {log.errorMessage}
                  </Alert>
                </Grid>
              )}
            </Grid>

            {bodyJson && (
              <>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Request payload
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: "grey.50",
                    maxHeight: 240,
                    overflow: "auto",
                  }}
                >
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{ m: 0, fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                  >
                    {bodyJson}
                  </Typography>
                </Paper>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              User who performed this action
            </Typography>
            <Grid container spacing={2}>
              <DetailRow
                label="Name"
                value={
                  log.user?.id ? (
                    <Link component={RouterLink} to={`/users/${log.user.id}`}>
                      {log.user.name}
                    </Link>
                  ) : (
                    log.userEmail || "—"
                  )
                }
              />
              <DetailRow label="Email" value={log.user?.email || log.userEmail} />
              <DetailRow label="Role" value={log.user?.role || log.userRole} />
              <DetailRow label="Phone" value={log.user?.phone} />
              <DetailRow
                label="Account status"
                value={
                  log.user?.isActive != null ? (
                    <Chip
                      size="small"
                      label={log.user.isActive ? "Active" : "Inactive"}
                      color={log.user.isActive ? "success" : "default"}
                    />
                  ) : (
                    "—"
                  )
                }
              />
              <DetailRow label="Branch" value={log.user?.branch?.name} />
              <DetailRow label="Sales point" value={log.user?.salesPoint?.name} />
              <DetailRow label="User ID" value={log.userId || log.user?.id} />
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {log?.user?.id && (
          <Button component={RouterLink} to={`/users/${log.user.id}`} onClick={onClose}>
            Open user profile
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AuditLogsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    resource: "all",
    action: "all",
    success: "all" as "all" | "true" | "false",
    search: "",
    startDate: null as Date | null,
    endDate: null as Date | null,
  });
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<AuditLogSortField>("createdAt", "desc");

  const queryKey = useMemo(
    () => [
      "audit-logs",
      page,
      rowsPerPage,
      filters.resource,
      filters.action,
      filters.success,
      filters.search,
      filters.startDate ? format(filters.startDate, "yyyy-MM-dd") : "",
      filters.endDate ? format(filters.endDate, "yyyy-MM-dd") : "",
      sortBy,
      sortOrder,
    ],
    [page, rowsPerPage, filters, sortBy, sortOrder]
  );

  const { data, isPending, isFetching, error } = useQuery({
    queryKey,
    queryFn: () =>
      auditLogsAPI.getAuditLogs({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        resource: filters.resource,
        action: filters.action,
        success:
          filters.success === "all"
            ? "all"
            : filters.success === "true",
        search: filters.search || undefined,
        startDate: filters.startDate
          ? format(filters.startDate, "yyyy-MM-dd")
          : undefined,
        endDate: filters.endDate
          ? format(filters.endDate, "yyyy-MM-dd")
          : undefined,
        ...sortParams,
      }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const isLoading = isPending || isFetching;
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const summaryBody = (body: Record<string, unknown> | null) => {
    if (!body) return "—";
    const text = JSON.stringify(body);
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
  };

  const onSort = (field: AuditLogSortField) =>
    handleSort(field, () => setPage(0));

  const openDetail = (row: AuditLogEntry) => {
    setSelectedLogId(row.id);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title="Audit logs"
          subtitle="Track who did what across the system (Super Admin only)"
          breadcrumbs={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Audit logs" },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error).message || "Failed to load audit logs"}
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Email, path, resource…"
                  value={filters.search}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, search: e.target.value }));
                    setPage(0);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Resource</InputLabel>
                  <Select
                    label="Resource"
                    value={filters.resource}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, resource: e.target.value }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="users">users</MenuItem>
                    <MenuItem value="auth">auth</MenuItem>
                    <MenuItem value="recharges">recharges</MenuItem>
                    <MenuItem value="cardholders">cardholders</MenuItem>
                    <MenuItem value="nfc-cards">nfc-cards</MenuItem>
                    <MenuItem value="settings">settings</MenuItem>
                    <MenuItem value="branches">branches</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    label="Action"
                    value={filters.action}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, action: e.target.value }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="CREATE">CREATE</MenuItem>
                    <MenuItem value="UPDATE">UPDATE</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                    <MenuItem value="READ">READ</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.success}
                    onChange={(e) => {
                      setFilters((f) => ({
                        ...f,
                        success: e.target.value as typeof filters.success,
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="true">Success</MenuItem>
                    <MenuItem value="false">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="From"
                  value={filters.startDate}
                  onChange={(d) => {
                    setFilters((f) => ({ ...f, startDate: d }));
                    setPage(0);
                  }}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="To"
                  value={filters.endDate}
                  onChange={(d) => {
                    setFilters((f) => ({ ...f, endDate: d }));
                    setPage(0);
                  }}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Click a row to view full details
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <SortableTableCell label="When" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="User" field="userEmail" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Role" field="userRole" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Action" field="action" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Resource" field="resource" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Method" field="httpMethod" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Path" field="path" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Status" field="success" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => openDetail(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatWhen(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      {row.user?.name || row.userEmail || row.userId || "—"}
                    </TableCell>
                    <TableCell>{row.userRole || row.user?.role || "—"}</TableCell>
                    <TableCell>
                      <Chip label={row.action} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{row.resource}</TableCell>
                    <TableCell>{row.httpMethod}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Typography variant="body2" noWrap title={row.path}>
                        {row.path}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.success ? "OK" : "Fail"}
                        size="small"
                        color={row.success ? "success" : "error"}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <Typography variant="caption" title={summaryBody(row.requestBody)}>
                        {row.errorMessage || summaryBody(row.requestBody)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>

        <AuditLogDetailDialog
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AuditLogsPage;
